import React from 'react';
import {
    DrawerList,
    DrawerSectionsWrapper,
    ResponsiveDrawer,
} from '../../buildDistribution/mobileComponents/ResponsiveDrawer';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors, tagStyle } from '../../styles/globalStyles';
import Space from 'antd/lib/space';
import { AppDevice, ASCRegistrationStatus, Device } from '../../../types';
import { MobileOutlined, CopyOutlined, ThunderboltFilled } from '@ant-design/icons';
import { displayASCRegistrationStatusDescription, displayDeviceClass } from '../../../helpers/displayHelpers';
import Button from 'antd/lib/button';
import { globalMessage } from '../../../utils/globalMessage';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import { tryUpdateDevicesRequest, UpdateDevicesRequest } from '../../../actions/devices';
import { DevicesUpdatable, OrgSettingsUpdatable } from '../../../actions/updatable';
import { useDispatch, useSelector } from 'react-redux';
import { RemoveUserDeviceRequest, tryUpdateOrgSettings } from '../../../actions/orgSettings';
import { selectOrg } from '../../../selectors/selectors';
import { RootState } from '../../../reducers/reducers';
import { OrgComponentProps } from '../../../typescriptHelpers';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

export const ShortUDIDComponent: React.FC<{ udid: string; maxWidth?: number }> = ({ udid, maxWidth }) => {
    return (
        <Space size={4}>
            <Button
                type="link"
                icon={
                    <CopyOutlined
                        style={{
                            color: runwayColors.primary,
                        }}
                    />
                }
                style={{
                    height: 0,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(udid);
                    globalMessage.success('Copied UDID to clipboard');
                }}
            />

            <Text
                ellipsis={{
                    tooltip: {
                        placement: 'left',
                        title: udid,
                    },
                }}
                style={{
                    maxWidth: maxWidth,
                    direction: 'rtl',
                }}
            >
                {udid}
            </Text>
        </Space>
    );
};

export const automationMessageForDevice = (device: AppDevice) => {
    let message = '';
    if (
        device?.ascRegistrationStatus === ASCRegistrationStatus.Enabled &&
        device?.registeredInASCByAutomationAt &&
        device?.updatedStatusInASCByAutomationAt
    ) {
        message = `Runway registered and enabled this device.`;
    } else if (
        device?.ascRegistrationStatus === ASCRegistrationStatus.Enabled &&
        device?.updatedStatusInASCByAutomationAt &&
        !device?.registeredInASCByAutomationAt
    ) {
        message = `Runway enabled this device.`;
    } else if (
        device?.ascRegistrationStatus === ASCRegistrationStatus.Disabled &&
        device?.updatedStatusInASCByAutomationAt
    ) {
        message = `Runway disabled this device.`;
    }
    if (!message) {
        return null;
    }

    return (
        <Space>
            <ThunderboltFilled style={{ color: runwayColors.primary }} />
            {message}
        </Space>
    );
};

export const DeviceStatusTag: React.FC<{
    device?: AppDevice;
    shouldShowAutomationStatusInsideTooltip?: boolean;
}> = ({ device, shouldShowAutomationStatusInsideTooltip }) => {
    const color =
        device?.ascRegistrationStatus ?
            device.ascRegistrationStatus === ASCRegistrationStatus.Enabled ?
                runwayColors.statusGreen
            :   antGrey.grey6
        :   antGrey.grey7;

    const tagComponent = (
        <Tag
            color="white"
            style={{
                ...tagStyle,
                fontSize: 10,
                borderColor: color,
                color: color,
            }}
        >
            {device.ascRegistrationStatus ?
                device.ascRegistrationStatus === ASCRegistrationStatus.Enabled ?
                    'Enabled'
                :   'Disabled'
            :   'Not registered'}
        </Tag>
    );

    return shouldShowAutomationStatusInsideTooltip ?
            <Tooltip
                placement="left"
                title={
                    <>
                        {displayASCRegistrationStatusDescription(device?.ascRegistrationStatus)}{' '}
                        {automationMessageForDevice(device)}
                    </>
                }
            >
                {tagComponent}
            </Tooltip>
        :   <Space direction="vertical" align="end">
                <Tooltip
                    placement="left"
                    title={displayASCRegistrationStatusDescription(device?.ascRegistrationStatus)}
                >
                    {tagComponent}
                </Tooltip>
                <Text type="secondary">{automationMessageForDevice(device)}</Text>
            </Space>;
};

export interface DeviceDetailsDrawerProps extends OrgComponentProps {
    device?: AppDevice | Device;
    appId?: string;
    onClose: () => void;
    afterOpenChange?: (boolean) => void;
    shouldShowRegistrationStatus: boolean;
}

// guard type
export function isAppDevice(device: AppDevice | Device): device is AppDevice {
    if (!device) {
        return false;
    }
    return 'associatedUserEmail' in device;
}

export const DeviceDetailsDrawer: React.FC<DeviceDetailsDrawerProps> = (props) => {
    const { onClose, device, afterOpenChange, appId, shouldShowRegistrationStatus } = props;

    const orgId = useSelector((state: RootState) => selectOrg(state).orgId);
    const dispatch = useDispatch();

    const title = device?.displayName;

    const isDeletingDevices = useIsLoading(DevicesUpdatable.DeleteDevices, appId);
    const deletingDevicesError = useError(DevicesUpdatable.DeleteDevices, appId);
    const deletingDevicesSuccess = useUpdated(DevicesUpdatable.DeleteDevices, appId);

    const isDeletingUserDevices = useIsLoading(OrgSettingsUpdatable.RemoveDeviceFromOrgUser, orgId);
    const deletingUserDevicesError = useError(OrgSettingsUpdatable.RemoveDeviceFromOrgUser, orgId);
    const deletingUserDevicesSuccess = useUpdated(OrgSettingsUpdatable.RemoveDeviceFromOrgUser, orgId);

    const isDeleting = isDeletingDevices || isDeletingUserDevices;
    const deletingError = deletingDevicesError || deletingUserDevicesError;
    const deletingSuccess = deletingDevicesSuccess || deletingUserDevicesSuccess;

    React.useEffect(() => {
        if (deletingSuccess) {
            onClose();
        }
    }, [deletingSuccess]);

    React.useEffect(() => {
        if (deletingError) {
            globalMessage.error(deletingError);
        }
    }, [deletingError]);

    return (
        <ResponsiveDrawer
            open={!!device}
            onClose={onClose}
            afterOpenChange={afterOpenChange}
            header={
                <Space size={20}>
                    <MobileOutlined
                        style={{
                            fontSize: 24,
                            color: runwayColors.primary,
                        }}
                    />
                    <Text
                        style={{
                            fontSize: 16,
                            color: antGrey.grey8,
                        }}
                    >
                        {title}
                    </Text>
                </Space>
            }
            content={
                <DrawerSectionsWrapper>
                    <Space
                        direction="vertical"
                        size={22}
                        style={{
                            width: '100%',
                            padding: 16,
                            marginTop: 16,
                        }}
                    >
                        {isAppDevice(device) && (
                            <DrawerList
                                items={[
                                    {
                                        title: 'Associated user',
                                        description: device?.associatedUserEmail ?? (
                                            <Text type="secondary">No associated user</Text>
                                        ),
                                    },
                                ]}
                            />
                        )}
                        <DrawerList
                            items={[
                                {
                                    title: 'Device type',
                                    description:
                                        device?.deviceClass ?
                                            displayDeviceClass(device?.deviceClass)
                                        :   <Text type="secondary">Unknown</Text>,
                                },
                                {
                                    title: 'UDID',
                                    description: <ShortUDIDComponent udid={device?.udid} />,
                                },
                            ]}
                        />
                        {shouldShowRegistrationStatus && isAppDevice(device) && (
                            <DrawerList
                                items={[
                                    {
                                        title: 'Status',
                                        description: (
                                            <DeviceStatusTag
                                                device={device}
                                                shouldShowAutomationStatusInsideTooltip={false}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        )}
                        {device?.isRegisteredInRunway && (
                            <Button
                                type="text"
                                loading={isDeleting}
                                style={{
                                    width: '100%',
                                    height: 'unset',
                                    padding: '9px 0',
                                    backgroundColor: antGrey.grey1,
                                }}
                                danger
                                onClick={() => {
                                    if (!device) {
                                        return;
                                    }

                                    if (appId) {
                                        const request: UpdateDevicesRequest = {
                                            deviceIds: [device.id],
                                            appId,
                                            orgId,
                                        };
                                        dispatch(tryUpdateDevicesRequest(DevicesUpdatable.DeleteDevices, request));
                                    } else {
                                        const request: RemoveUserDeviceRequest = {
                                            deviceIds: [device.id],
                                            orgId,
                                        };

                                        dispatch(
                                            tryUpdateOrgSettings(
                                                OrgSettingsUpdatable.RemoveDeviceFromOrgUser,
                                                request,
                                                orgId,
                                            ),
                                        );
                                    }
                                }}
                            >
                                Remove this device
                            </Button>
                        )}
                    </Space>
                </DrawerSectionsWrapper>
            }
        />
    );
};
